/**
 * Public API service for unauthenticated requests.
 *
 * Used for the public demo analysis feature.
 * No authentication tokens are attached to requests.
 *
 * Note: The claim endpoint requires authentication and uses the
 * authenticated API instance from './api'.
 */

import axios from 'axios'
import { api } from './api'

const API_URL = import.meta.env.VITE_API_URL || '/api'

// Separate axios instance without auth interceptors
export const publicApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Types for public analysis API
export interface PublicAnalysisStartRequest {
  website: string
}

export interface PublicAnalysisStartResponse {
  id: string
  status: string
  cached: boolean
}

export interface SourceSuggestion {
  url: string
  name: string
  description: string
  source_type: string
  region: string
  relevance_score: number
  recommended_search_terms?: string[]
}

export interface SuggestedSourcesData {
  sources: SourceSuggestion[]
  reasoning: string
}

export interface CompanyProfileData {
  industry_tags: string[]
  target_markets: string[]
  business_category: string | null
  prospecting_goals: string[]
  suggested_name: string | null
  suggested_list_query?: string | null
  website_language?: string | null
}

export interface IdealCustomerProfileData {
  services_offered: string[]
  target_customer_industries: string[]
  target_company_types: string[]
  target_company_size: string[]
  value_proposition: string
  buyer_persona: string[]
  geographic_focus: string[]
}

export interface PublicAnalysisResponse {
  id: string
  status: 'pending' | 'crawling' | 'analyzing' | 'completed' | 'failed'
  website_url: string
  domain: string
  company_profile: CompanyProfileData | null
  ideal_customer_profile: IdealCustomerProfileData | null
  suggested_sources: SuggestedSourcesData | null
  confidence: number
  error: string | null
  created_at: string | null
  completed_at: string | null
}

// Public Analysis API
export const publicAnalysisApi = {
  /**
   * Start a new website analysis.
   * Returns immediately with an analysis ID that can be polled for results.
   */
  startAnalysis: async (
    website: string
  ): Promise<PublicAnalysisStartResponse> => {
    const response = await publicApi.post<PublicAnalysisStartResponse>(
      '/v1/public/analyze',
      { website }
    )
    return response.data
  },

  /**
   * Get the current status and results of an analysis.
   * Poll this until status is 'completed' or 'failed'.
   */
  getAnalysis: async (analysisId: string): Promise<PublicAnalysisResponse> => {
    const response = await publicApi.get<PublicAnalysisResponse>(
      `/v1/public/analysis/${analysisId}`
    )
    return response.data
  },

  /**
   * Claim a public analysis for the authenticated user.
   *
   * This links the analysis to the user's account so the onboarding flow
   * can pre-populate organization data without re-running the analysis.
   *
   * NOTE: This endpoint requires authentication and uses the authenticated
   * API instance, not the public one.
   *
   * @param analysisId - The UUID of the analysis to claim
   * @returns The claimed analysis data, or null if claim failed
   */
  claimAnalysis: async (
    analysisId: string
  ): Promise<PublicAnalysisResponse | null> => {
    try {
      const response = await api.post<PublicAnalysisResponse>(
        `/v1/public/analysis/${analysisId}/claim`
      )
      return response.data
    } catch {
      // Claim failed (analysis not found, already claimed, etc.)
      // Return null to allow fallback to normal onboarding flow
      return null
    }
  },
}
